import { 
  PlusIcon, 
  SparklesIcon, 
  PlayIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { BottomNavigationBar } from '../../components/BottomNavigationBar';
import { FeedbackButtons } from '../../components/chat';
import { ExecutionPlanPanel } from '../../components/ExecutionPlanPanel';
import { MonacoMarkdownEditor } from '../../components/monaco/MonacoMarkdownEditor';
import { PageHeader } from '../../components/PageHeader';
import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import {
  BACK_BUTTON_LABEL,
  CONTINUE_BUTTON_LABEL,
} from '../../constants/navigation';
import {
  useAgentJobCompletion,
  useAgentJobs,
  useAgentJobsApi,
} from '../../hooks/useAgentJobs';
import { adrService, ADRSuggestRequest } from '../../services/adrService';
import {
  getADRSuggestionsCache,
  setADRSuggestionsCache,
} from '../../services/adrSuggestionsCache';
import { notifyAdrUpdated } from '../../services/adrUpdateEvents';
import { AgentJobType, adrGenerationTarget } from '../../services/agentJobTargets';
import { getBrownfieldAdrSuggestions } from '../../services/brownfieldConversionService';
import { projectService } from '../../services/projectService';
import { 
  ADRSuggestion,
  ADRDefinition, 
  ADRDefinitionStatus,
  ADRSource,
  ADRDefinitionUpsert,
} from '../../types/adr';
import { ProjectStatus, ProjectType } from '../../types/project';
import { logger } from '../../utils/logger';

import {
  shouldAutoGenerateSuggestions,
  shouldShowAutoSave,
  canSaveADR,
  isADRGenerating,
  isGenerateAllDisabled,
  hasDefinedADRs,
  buildProjectContext,
  createDefinitionFromForm,
  formatFormDataForEdit,
  getInitialFormData,
  parseADRSections,
  formatADRReference,
} from './ADRDefinitionPageHelpers';
import { useADRActiveTab } from './useADRActiveTab';
import { useADRAutoSave } from './useADRAutoSave';
import { useADRDefinitionListener } from './useADRDefinitionListener';
import { useADRState } from './useADRState';
import { useADRUpdateListener } from './useADRUpdateListener';
import { useLoadADRData } from './useLoadADRData';

import { AIDisclaimer } from '@/components/AIDisclaimer';
// Constants for consistent styling
const THEME_COLORS = {
  primary: '#336094',
  primaryHover: '#2a4d7a',
} as const;

const BUTTON_STYLES = {
  primary: 'inline-flex items-center px-4 py-2 rounded-md text-sm transition-colors main-button',
  primaryDisabled: 'inline-flex items-center px-4 py-2 bg-gray-400 text-white rounded-md text-sm cursor-not-allowed',
  secondary: 'inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-yellow-50 hover:border-yellow-200 transition-colors',
} as const;

const INPUT_STYLES = {
  base: `w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[${THEME_COLORS.primary}] focus:border-transparent`,
  textarea: `w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[${THEME_COLORS.primary}] focus:border-transparent resize-vertical`,
} as const;

// Timeouts and delays
const SAVE_SUCCESS_DISPLAY_MS = 2000;
const DEFAULT_PROJECT_ID = 1;

// Text constants
const YOUR_PROJECT_TEXT = 'your project';
const PRIMARY_DISABLED_BUTTON_CLASS = `${BUTTON_STYLES.primary} disabled:bg-gray-300 main-button`;
const ICON_BASE_CLASS = 'w-4 h-4 mr-2';
const ANIMATE_SPIN_CLASS = 'animate-spin';
// Styling for the destructive delete-row icon button in the My ADRs table.
const DELETE_ROW_BUTTON_CLASS =
  'inline-flex items-center justify-center p-2 border border-red-300 ' +
  'rounded text-red-700 bg-red-50 hover:bg-red-100 transition-colors ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const ADRDefinitionPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState<'suggestions' | 'definitions'>('suggestions');

  // Use the custom state hook to manage all ADR-related state
  const adrState = useADRState();

  // Navigation-resilient agent-job tracker. Generation polling is owned by the
  // app-root tracker (not this page), so a run continues if the user navigates
  // away; this page reconciles its spinner/results when it remounts.
  const jobs = useAgentJobsApi();
  const trackedJobs = useAgentJobs(projectId ? Number.parseInt(projectId, 10) : -1);

  // On (re)mount, restore the per-row "generating" spinner for any ADR whose
  // generation job is still in flight in the tracker.
  useEffect(() => {
    const activeAdrIds = trackedJobs
      .filter(
        (job) =>
          job.jobType === AgentJobType.ADR_GENERATION &&
          (job.status === 'pending' || job.status === 'running'),
      )
      .map((job) => job.target ?? '')
      .filter((target) => target.startsWith('adr:'))
      .map((target) => target.slice('adr:'.length));
    if (activeAdrIds.length === 0) {return;}
    adrState.setGeneratingADRIds((prev) => {
      const next = new Set(prev);
      activeAdrIds.forEach((id) => next.add(id));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedJobs]);

  // When a tracked ADR generation finishes (even while this page was
  // unmounted), re-dispatch the existing domain events so the spinner clears
  // and the definitions/content refetch via the listeners below.
  useAgentJobCompletion(
    (descriptor) =>
      descriptor.jobType === AgentJobType.ADR_GENERATION &&
      !!projectId &&
      descriptor.projectId === Number.parseInt(projectId, 10),
    (descriptor) => {
      const adrId = descriptor.target?.startsWith('adr:')
        ? descriptor.target.slice('adr:'.length)
        : undefined;
      if (!adrId || !projectId) {return;}
      const eventName =
        descriptor.status === 'succeeded'
          ? 'adrContentGenerated'
          : 'adrGenerationFailed';
      globalThis.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { adrId, projectId: Number.parseInt(projectId, 10) },
        }),
      );
    },
  );

  // Ref to prevent duplicate API calls
  const isGeneratingRef = useRef(false);
  const hasAutoGeneratedRef = useRef(false);

  // State for execution plan display (not in useADRState)
  const [adrExecutionPlan, setAdrExecutionPlan] = useState<Array<Record<string, unknown>> | Array<string> | null>(null);
  const [isExecutionPlanExpanded, setIsExecutionPlanExpanded] = useState<boolean>(false);

  // Parsed Context/Decision/Consequence per ADR id, derived from the generated
  // markdown, used to populate the My ADRs table columns.
  const [adrSectionsMap, setAdrSectionsMap] = useState<Record<string, { context: string; decision: string; consequence: string }>>({});
  // Local draft values for the user-editable governance columns (Step-Out,
  // Approval date, Approved by) keyed by ADR id, so typing stays responsive.
  const [governanceDrafts, setGovernanceDrafts] = useState<Record<string, { stepOut: string; approvalDate: string; approvedBy: string }>>({});

  // Restore suggestions state from the module-level cache on mount so that
  // navigating away and back does not trigger a fresh generation.
  useEffect(() => {
    if (!projectId) {return;}
    const cached = getADRSuggestionsCache(projectId);
    if (!cached) {return;}

    if (cached.hasAutoGenerated) {
      hasAutoGeneratedRef.current = true;
    }
    if (cached.suggestions.length > 0) {
      adrState.setSuggestions(cached.suggestions);
    }
    if (cached.adrSuggestionData.inputs ?? cached.adrSuggestionData.outputs) {
      adrState.setAdrSuggestionData(cached.adrSuggestionData);
    }
    if (cached.suggestionProjectContext) {
      adrState.setSuggestionProjectContext(cached.suggestionProjectContext);
    }
    if (cached.adrExecutionPlan) {
      setAdrExecutionPlan(cached.adrExecutionPlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally runs only on mount; projectId captured via closure

  // Seed the editable governance drafts whenever the definitions change so the
  // table inputs reflect the persisted Step-Out / Approval date / Approved by.
  useEffect(() => {
    setGovernanceDrafts((prev) => {
      const next = { ...prev };
      for (const def of adrState.definitions) {
        if (!next[def.id]) {
          next[def.id] = {
            stepOut: def.stepOut ?? '',
            approvalDate: def.approvalDate ?? '',
            approvedBy: def.approvedBy ?? '',
          };
        }
      }
      return next;
    });
  }, [adrState.definitions]);

  // Load and parse the generated Context/Decision/Consequence for each ADR so
  // the My ADRs table can display them as columns.
  useEffect(() => {
    if (!projectId) {return;}
    let cancelled = false;

    const loadSections = async () => {
      const entries = await Promise.all(
        adrState.definitions.map(async (def) => {
          if (def.status === ADRDefinitionStatus.DEFINED) {
            return [def.id, { context: '', decision: '', consequence: '' }] as const;
          }
          try {
            const content = await adrService.getADRGeneratedContent(
              Number.parseInt(projectId, 10),
              def.id,
            );
            return [def.id, parseADRSections(content)] as const;
          } catch {
            return [def.id, { context: '', decision: '', consequence: '' }] as const;
          }
        }),
      );
      if (!cancelled) {
        setAdrSectionsMap(Object.fromEntries(entries));
      }
    };

    void loadSections();
    return () => {
      cancelled = true;
    };
  }, [adrState.definitions, projectId]);

  /**
   * Persist a user edit to one of the governance columns (Step-Out, Approval
   * date, Approved by). The agent never fills these; only the user does. The
   * generated ADR markdown is preserved by the backend on update.
   */
  const handleGovernanceSave = useCallback(
    async (definition: ADRDefinition) => {
      if (!projectId) {return;}
      const draft = governanceDrafts[definition.id];
      if (!draft) {return;}
      try {
        const updated = await adrService.saveADRDefinition(
          Number.parseInt(projectId, 10),
          {
            title: definition.title,
            llmInstructions: definition.llmInstructions,
            contextDescription: definition.contextDescription ?? '',
            source: definition.source,
            status: definition.status,
            stepOut: draft.stepOut,
            approvalDate: draft.approvalDate,
            approvedBy: draft.approvedBy,
          },
          definition.id,
        );
        adrState.setDefinitions((prev) =>
          prev.map((d) => (d.id === definition.id ? updated : d)),
        );
      } catch (error) {
        logger.error('Failed to save ADR governance fields:', error);
        adrState.showErrorMessage('Failed to save ADR field. Please try again.');
      }
    },
    [projectId, governanceDrafts, adrState],
  );

  /**
   * Update a single governance draft field locally (debounced persistence
   * happens on blur via handleGovernanceSave).
   */
  const handleGovernanceChange = useCallback(
    (
      adrId: string,
      field: 'stepOut' | 'approvalDate' | 'approvedBy',
      value: string,
    ) => {
      setGovernanceDrafts((prev) => ({
        ...prev,
        [adrId]: {
          stepOut: prev[adrId]?.stepOut ?? '',
          approvalDate: prev[adrId]?.approvalDate ?? '',
          approvedBy: prev[adrId]?.approvedBy ?? '',
          [field]: value,
        },
      }));
    },
    [],
  );

  const navigate = useNavigate();

  const handleStageClick = (stageId: number, route: string) => {
    navigate(route);
  };

  // Load ADR data when component mounts
  useLoadADRData({
    projectId,
    onProjectLoaded: (project) => {
      adrState.setProject(project as unknown as typeof adrState.project);
    },
    onDefinitionsLoaded: adrState.setDefinitions,
    onLoadingStateChange: adrState.setIsLoadingDefinitions,
  });

  // Listen for ADR definition creation events
  useADRDefinitionListener({
    projectId,
    onDefinitionCreated: (definition) => {
      adrState.setDefinitions(prev => {
        const exists = prev.some(item => item.id === definition.id);
        if (exists) {
          return prev.map(item => (item.id === definition.id ? definition : item));
        }
        return [...prev, definition];
      });
    },
  });

  // Listen for ADR update events
  useADRUpdateListener({
    projectId,
    onDefinitionsUpdated: adrState.setDefinitions,
  });

  // Setup event listeners for ADR content generation, suggestions, etc.
  useEffect(() => {
    const handleContentGenerated = (event: Event) => {
      const customEvent = event as CustomEvent<{
        adrId?: string;
        projectId?: number | string;
      }>;
      const { adrId, projectId: eventProjectId } = customEvent.detail ?? {};

      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        return;
      }

      if (adrId) {
        adrState.setGeneratingADRIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(adrId);
          return newSet;
        });
      }

      const refreshDefinitions = async () => {
        if (!projectId) {
          return;
        }

        try {
          const updatedDefinitions = await adrService.getADRDefinitions(Number(projectId));
          adrState.setDefinitions(updatedDefinitions);
        } catch (err) {
          logger.error('Failed to refresh ADR definitions:', err);
        }
      };

      refreshDefinitions();
    };

    const handleGenerationStarted = (event: Event) => {
      const customEvent = event as CustomEvent<{
        adrId?: string;
        projectId?: number | string;
      }>;
      const { adrId, projectId: eventProjectId } = customEvent.detail ?? {};

      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        return;
      }

      if (adrId) {
        adrState.setGeneratingADRIds(prev => new Set(prev).add(adrId));
      }
    };

    const handleGenerationFailed = (event: Event) => {
      const customEvent = event as CustomEvent<{
        adrId?: string;
        projectId?: number | string;
      }>;
      const { adrId, projectId: eventProjectId } = customEvent.detail ?? {};

      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        return;
      }

      if (adrId) {
        adrState.setGeneratingADRIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(adrId);
          return newSet;
        });
      }
    };

    const handleSuggestionsCompleted = (event: Event) => {
      const customEvent = event as CustomEvent<{
        projectId?: number | string;
        suggestions?: ADRSuggestion[];
        executionTrace?: {
          steps?: Array<Record<string, unknown>>;
        };
      }>;
      const { projectId: eventProjectId, suggestions, executionTrace } = customEvent.detail ?? {};

      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        return;
      }

      if (suggestions) {
        adrState.setSuggestions(suggestions);
        if (projectId) {
          setADRSuggestionsCache(projectId, {
            suggestions,
            hasAutoGenerated: true,
          });
        }
      }

      // Store execution trace from the response
      if (executionTrace?.steps) {
        const stepsWithNumbers = executionTrace.steps.map((step, index) => ({
          step: index + 1,
          ...step,
        }));
        setAdrExecutionPlan(stepsWithNumbers);
        if (projectId) {
          setADRSuggestionsCache(projectId, { adrExecutionPlan: stepsWithNumbers });
        }
      }

      // Stop the spinner and switch to suggestions tab
      adrState.setIsGeneratingSuggestions(false);
      setActiveTab('suggestions'); // Switch to suggestions tab to show the new suggestions
    };    const handleSuggestionsStarted = (event: Event) => {
      const customEvent = event as CustomEvent<{ 
        projectId?: number | string 
      }>;
      const { projectId: eventProjectId } = customEvent.detail ?? {};

      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        return;
      }

      adrState.setIsGeneratingSuggestions(true);
    };

    const handleSuggestionsFailed = (event: Event) => {
      const customEvent = event as CustomEvent<{
        projectId?: number | string;
      }>;
      const { projectId: eventProjectId } = customEvent.detail ?? {};

      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        return;
      }

      adrState.setIsGeneratingSuggestions(false);
    };

    globalThis.addEventListener('adrContentGenerated', handleContentGenerated as EventListener);
    globalThis.addEventListener('adrGenerationStarted', handleGenerationStarted as EventListener);
    globalThis.addEventListener('adrGenerationFailed', handleGenerationFailed as EventListener);
    globalThis.addEventListener('adrSuggestionsCompleted', handleSuggestionsCompleted as EventListener);
    globalThis.addEventListener('adrSuggestionsStarted', handleSuggestionsStarted as EventListener);
    globalThis.addEventListener('adrSuggestionsFailed', handleSuggestionsFailed as EventListener);

    return () => {
      globalThis.removeEventListener('adrContentGenerated', handleContentGenerated as EventListener);
      globalThis.removeEventListener('adrGenerationStarted', handleGenerationStarted as EventListener);
      globalThis.removeEventListener('adrGenerationFailed', handleGenerationFailed as EventListener);
      globalThis.removeEventListener('adrSuggestionsCompleted', handleSuggestionsCompleted as EventListener);
      globalThis.removeEventListener('adrSuggestionsStarted', handleSuggestionsStarted as EventListener);
      globalThis.removeEventListener('adrSuggestionsFailed', handleSuggestionsFailed as EventListener);
    };
  }, [projectId, adrState]);

  // Set default tab based on definitions state
  useADRActiveTab({
    isLoadingDefinitions: adrState.isLoadingDefinitions,
    definitionsCount: adrState.definitions.length,
    onTabChange: setActiveTab,
  });

  // Auto-save ADR content
  useADRAutoSave({
    hasUnsavedChanges: adrState.hasUnsavedChanges,
    editingADR: adrState.editingADR,
    shouldSave: shouldShowAutoSave,
    onAutoSave: () => {
       
      handleSaveADR();
    },
  });

  // ADR Editor functions
  const getADRContent = async (adrId: string): Promise<string> => {
    try {
      return await adrService.getADRGeneratedContent(Number.parseInt(projectId!, 10), adrId);
    } catch (error) {
      logger.error('Failed to get ADR content:', error);
      throw error;
    }
  };

  const handleADRContentChange = (value: string) => {
    adrState.setAdrContent(value);
    adrState.setHasUnsavedChanges(true);
  };

  const handleSaveADR = async () => {
    // Early return for invalid conditions
    if (!canSaveADR(adrState.editingADR, projectId, adrState.adrContent) || !adrState.editingADR || !projectId) {
      logger.warn('Cannot save ADR: missing required data', {
        editingADR: !!adrState.editingADR,
        projectId: !!projectId,
        adrContent: !!adrState.adrContent,
      });
      return;
    }

    adrState.setIsSaving(true);

    try {

      await adrService.saveADRGeneratedContent(Number.parseInt(projectId, 10), adrState.editingADR.id, adrState.adrContent);

      adrState.setHasUnsavedChanges(false);
      adrState.setLastSaved(new Date());

      adrState.setJustSaved(true);
      setTimeout(() => adrState.setJustSaved(false), SAVE_SUCCESS_DISPLAY_MS);

      // Notify that ADR content was updated
      notifyAdrUpdated(Number.parseInt(projectId, 10), adrState.editingADR.id);

    } catch (error) {
      logger.error('Failed to save ADR:', error);
      adrState.showErrorMessage('Failed to save ADR. Please try again.');
    } finally {
      adrState.setIsSaving(false);
    }
  };

  const handleCloseADRViewer = () => {
    adrState.setShowADRViewer(false);
    adrState.setEditingADR(null);
    adrState.setAdrContent('');
    adrState.setHasUnsavedChanges(false);
    adrState.setJustSaved(false);
  };
    // Suggestion handling functions
  const createDefinitionFromSuggestion = (suggestion: ADRSuggestion): ADRDefinitionUpsert => ({
    title: suggestion.title,
    llmInstructions: `Generate an ADR for: ${suggestion.title}. ${suggestion.reasoning}`,
    contextDescription: suggestion.description,
    source: ADRSource.AI_SUGGESTED,
    status: ADRDefinitionStatus.DEFINED,
  });

  /**
   * Auto-generate an accepted ADR's content so it appears fully populated
   * (Context / Decision / Consequence) in the My ADRs table. Reloads the
   * definitions afterwards, which re-triggers the section parsing effect.
   */
  const autoGenerateAcceptedADR = async (adrId: string) => {
    if (!projectId) {return;}
    adrState.setGeneratingADRIds((prev) => new Set(prev).add(adrId));
    try {
      await jobs.startJobAndWait({
        projectId: Number.parseInt(projectId, 10),
        jobType: AgentJobType.ADR_GENERATION,
        target: adrGenerationTarget(adrId),
        label: 'Generating ADR',
        startFn: adrService.startGenerateADR(Number.parseInt(projectId, 10), adrId),
      });
      const refreshed = await adrService.getADRDefinitions(Number.parseInt(projectId, 10));
      adrState.setDefinitions(refreshed);
      notifyAdrUpdated(Number.parseInt(projectId, 10), adrId);
    } catch (genError) {
      logger.error('Auto-generation after accept failed:', genError);
    } finally {
      adrState.setGeneratingADRIds((prev) => {
        const next = new Set(prev);
        next.delete(adrId);
        return next;
      });
    }
  };

  const handleAcceptSuggestion = async (suggestion: ADRSuggestion) => {
    // Early return for missing projectId
    if (!projectId) {return;}

    try {
      const newDefinition = createDefinitionFromSuggestion(suggestion);
      const savedDefinition = await adrService.saveADRDefinition(Number.parseInt(projectId, 10), newDefinition, null);

      adrState.setDefinitions(prev => [...prev, savedDefinition]);
      const remainingSuggestions = adrState.suggestions.filter(
        s => s.id !== suggestion.id,
      );
      adrState.setSuggestions(remainingSuggestions);
      setADRSuggestionsCache(projectId, { suggestions: remainingSuggestions });

      // Notify that ADR was accepted/created and persist timestamp
      const acceptTimestamp = new Date().toISOString();
      notifyAdrUpdated(Number.parseInt(projectId, 10), savedDefinition.id);

      // Also persist the timestamp in project state for cross-page consistency
      try {
        const { projectStateService } = await import('../../services/projectStateService');
        await projectStateService.updateAdrActionTimestamp(Number.parseInt(projectId, 10), acceptTimestamp);
      } catch (error) {
        logger.error('[ADR Accept] Failed to persist accept timestamp:', error);
      }

      // Accepting moves the ADR into the My ADRs table and auto-generates it.
      await autoGenerateAcceptedADR(savedDefinition.id);
    } catch (error) {
      logger.error('Failed to save ADR definition:', error);
      adrState.showErrorMessage('Failed to accept suggestion. Please try again.');
    }
  };

  const handleAcceptAllSuggestions = async () => {
    // Early return for missing projectId or no suggestions
    if (!projectId || adrState.suggestions.length === 0) {
      return;
    }

    adrState.setIsAcceptingAll(true);
    try {
      const acceptTimestamp = new Date().toISOString();
      const newDefinitions: ADRDefinition[] = [];

      // Accept all suggestions sequentially to avoid race conditions
      for (const suggestion of adrState.suggestions) {
        try {
          const newDefinition = createDefinitionFromSuggestion(suggestion);
          const savedDefinition = await adrService.saveADRDefinition(Number.parseInt(projectId, 10), newDefinition, null);
          newDefinitions.push(savedDefinition);

          // Notify that ADR was accepted/created
          notifyAdrUpdated(Number.parseInt(projectId, 10), savedDefinition.id);
        } catch (error) {
          logger.error(`Failed to accept suggestion: ${suggestion.title}`, error);
          // Continue with other suggestions even if one fails
        }
      }

      // Update state with all new definitions
      adrState.setDefinitions(prev => [...prev, ...newDefinitions]);
      adrState.setSuggestions([]);
      setADRSuggestionsCache(projectId, { suggestions: [] });

      // Persist the timestamp in project state for cross-page consistency
      try {
        const { projectStateService } = await import('../../services/projectStateService');
        await projectStateService.updateAdrActionTimestamp(Number.parseInt(projectId, 10), acceptTimestamp);
      } catch (error) {
        logger.error('[ADR Accept All] Failed to persist accept timestamp:', error);
      }

      // Auto-generate each accepted ADR so they populate the My ADRs table.
      for (const def of newDefinitions) {
        await autoGenerateAcceptedADR(def.id);
      }
    } catch (error) {
      logger.error('Failed to accept all suggestions:', error);
      adrState.showErrorMessage('Failed to accept all suggestions. Please try again.');
    } finally {
      adrState.setIsAcceptingAll(false);
    }
  };

  const handleSkipSuggestion = (suggestionId: string) => {
    const remaining = adrState.suggestions.filter(s => s.id !== suggestionId);
    adrState.setSuggestions(remaining);
    if (projectId) {
      setADRSuggestionsCache(projectId, { suggestions: remaining });
    }
  };

  /**
   * Advance the project into the generation phase once ADR suggestions exist.
   * Reaching the ADR stage (even without selecting any suggestion) means the
   * project has progressed past context review, so its status moves forward.
   */
  const advanceToGenerationStatus = useCallback(async () => {
    if (!projectId) {return;}
    const { projectStateService } = await import('../../services/projectStateService');
    await projectStateService.advanceProjectStatus(
      Number.parseInt(projectId, 10),
      ProjectStatus.ADR,
    );
  }, [projectId]);

  const handleGenerateAISuggestions = useCallback(async () => {
    if (!projectId) {return;}
    
    // Prevent duplicate requests using ref
    if (isGeneratingRef.current) {
      logger.info('Request already in flight, skipping duplicate call');
      return;
    }
    
    isGeneratingRef.current = true;
    adrState.setIsGeneratingSuggestions(true);
    const startTime = Date.now();

    try {
      // For brownfield projects, the converted ADR section is the source of truth.
      // Load those first; only fall back to the AI agent if no conversion exists.
      const isBrownfield = adrState.project?.projectType === ProjectType.BROWN_FIELD;
      if (isBrownfield) {
        const brownfieldSuggestions = await getBrownfieldAdrSuggestions(
          Number.parseInt(projectId, 10),
        );
        if (brownfieldSuggestions.length > 0) {
          adrState.setSuggestions(brownfieldSuggestions);
          const brownfieldSuggestionData = {
            inputs: {
              projectId: Number.parseInt(projectId, 10),
              projectContext: '',
              maxSuggestions: brownfieldSuggestions.length,
              timestamp: new Date().toISOString(),
              userInitiated: true,
            },
            outputs: {
              suggestions: brownfieldSuggestions,
              processingTime: Date.now() - startTime,
              timestamp: new Date().toISOString(),
            },
          };
          adrState.setAdrSuggestionData(brownfieldSuggestionData);
          setADRSuggestionsCache(projectId, {
            suggestions: brownfieldSuggestions,
            hasAutoGenerated: true,
            adrSuggestionData: brownfieldSuggestionData,
          });
          await advanceToGenerationStatus();
          return;
        }
      }

      // Build project context from actual project data
      const projectContext = buildProjectContext(adrState.project);

      // Build ADR suggestion request
      const request: ADRSuggestRequest = {
        projectContext,
        maxSuggestions: 4,
        existingADRs: adrState.definitions.map(def => ({
          title: def.title,
        })),
      };

      // Capture input data for feedback
      const inputData = {
        projectId: Number.parseInt(projectId, 10),
        projectContext,
        maxSuggestions: request.maxSuggestions ?? 4,
        existingADRsCount: adrState.definitions.length,
        timestamp: new Date().toISOString(),
        userInitiated: true,
      };

      // Store the context used for generating suggestions
      adrState.setSuggestionProjectContext(projectContext);
      
      const response = await adrService.suggestADRs(Number.parseInt(projectId, 10), request);
      
      const processingTime = Date.now() - startTime;

      // Capture output data for feedback
      const outputData = {
        suggestions: response.suggestions,
        processingTime,
        timestamp: new Date().toISOString(),
      };

      // Store input/output data for feedback
      adrState.setAdrSuggestionData({
        inputs: inputData,
        outputs: outputData,
      });
      
      const suggestionsWithIds = response.suggestions.map((suggestion, index) => ({
        ...suggestion,
        id: suggestion.id ?? `suggestion-${Date.now()}-${index}`,
      }));
      
      adrState.setSuggestions(suggestionsWithIds);
      const executionPlan = response.execution_trace?.steps ?? null;
      if (executionPlan) {
        setAdrExecutionPlan(executionPlan);
      }
      setADRSuggestionsCache(projectId, {
        suggestions: suggestionsWithIds,
        hasAutoGenerated: true,
        adrSuggestionData: { inputs: inputData, outputs: outputData },
        suggestionProjectContext: projectContext,
        adrExecutionPlan: executionPlan,
      });
      await advanceToGenerationStatus();
    } catch (error) {
      logger.error('Failed to generate ADR suggestions:', error);
      adrState.showErrorMessage('Failed to generate suggestions. Please try again.');

      // Capture error information for feedback
      adrState.setAdrSuggestionData(prev => ({
        inputs: prev.inputs,
        outputs: {
          suggestions: [],
          processingTime: Date.now() - startTime,
          timestamp: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error',
        } as any,
      }));
    } finally {
      adrState.setIsGeneratingSuggestions(false);
      isGeneratingRef.current = false;
    }
  }, [projectId, adrState.project, adrState.definitions, advanceToGenerationStatus]);

  // Load ADR suggestions using adrService - only once on mount
  useEffect(() => {
    const isBrownfield =
      adrState.project?.projectType === ProjectType.BROWN_FIELD;
    if (shouldAutoGenerateSuggestions(
      adrState.isLoadingDefinitions,
      adrState.suggestions.length,
      adrState.isGeneratingSuggestions,
      adrState.definitions.length,
      isBrownfield,
    ) && !hasAutoGeneratedRef.current) {
      hasAutoGeneratedRef.current = true;
      if (projectId) {
        setADRSuggestionsCache(projectId, { hasAutoGenerated: true });
      }
      handleGenerateAISuggestions();
    }
  }, [adrState.isLoadingDefinitions, adrState.project, handleGenerateAISuggestions]); // project added so brownfield check sees the loaded project type

  // Form handling functions
  const resetForm = () => {
    adrState.setFormData(getInitialFormData());
  };

  // Helper function to save definition (extracted from handleFormSubmit)
  const saveDefinition = async (newDefinition: ADRDefinitionUpsert) => {
    if (!projectId) {throw new Error('Project ID is required');}

    if (adrState.editingDefinition) {
      const savedDefinition = await adrService.saveADRDefinition(
        Number.parseInt(projectId, 10),
        newDefinition,
        adrState.editingDefinition.id,
      );
      adrState.setDefinitions(prev =>
        prev.map(d =>
          d.id === adrState.editingDefinition!.id ? savedDefinition : d,
        ),
      );
      // Notify that ADR was updated
      notifyAdrUpdated(Number.parseInt(projectId, 10), adrState.editingDefinition.id);
    } else {
      const savedDefinition = await adrService.saveADRDefinition(
        Number.parseInt(projectId, 10),
        newDefinition,
        null,
      );
      adrState.setDefinitions(prev => [...prev, savedDefinition]);
      // Notify that ADR was created
      notifyAdrUpdated(Number.parseInt(projectId, 10), savedDefinition.id);
      // A newly defined ADR moves into the My ADRs table and immediately
      // starts generating its Context / Decision / Consequence. Fire-and-
      // forget so the definition modal can close right away — the table row
      // shows the "Generating" status while this runs in the background.
      void autoGenerateAcceptedADR(savedDefinition.id);
    }
  };

  const createDefinitionFromFormLocal = (): ADRDefinitionUpsert =>
    createDefinitionFromForm(adrState.formData);

  const handleFormSubmit = async () => {
    // Early return for missing projectId
    if (!projectId) {return;}

    try {
      const newDefinition = createDefinitionFromFormLocal();
      await saveDefinition(newDefinition);
      closeDefinitionModal();
    } catch (error) {
      logger.error('Failed to save ADR definition:', error);
      adrState.showErrorMessage('Failed to save ADR definition. Please try again.');
    }
  };

  const openEditDefinition = (definition: ADRDefinition) => {
    adrState.setFormData(formatFormDataForEdit(definition));
    adrState.setEditingDefinition(definition);
    adrState.setShowDefinitionModal(true);
  };

  const openNewDefinition = () => {
    adrState.setEditingDefinition(null);
    adrState.setShowDefinitionModal(true);
  };

  const closeDefinitionModal = () => {
    adrState.setShowDefinitionModal(false);
    adrState.setEditingDefinition(null);
    resetForm();
  };

  // ADR Management functions
  const handleGenerateSingleADR = async (adrId: string) => {
    const startTime = Date.now();

    try {
      if (!projectId) {return;}

      // Find the definition being generated
      const adrDefinition = adrState.definitions.find((d: ADRDefinition) => d.id === adrId);
      if (!adrDefinition) {
        logger.error('ADR definition not found:', adrId);
        return;
      }

      // Capture input data for feedback
      const inputData = {
        projectId: Number.parseInt(projectId, 10),
        adrDefinition,
        timestamp: new Date().toISOString(),
        userInitiated: true,
      };

      adrState.setGeneratingADRIds(prev => new Set(prev).add(adrId));

      await jobs.startJobAndWait({
        projectId: Number.parseInt(projectId, 10),
        jobType: AgentJobType.ADR_GENERATION,
        target: adrGenerationTarget(adrId),
        label: `Generating ADR: ${adrDefinition.title}`,
        startFn: adrService.startGenerateADR(Number.parseInt(projectId, 10), adrId),
      });
      const def = (await adrService.getADRDefinitions(Number.parseInt(projectId, 10))).find((d: ADRDefinition) => d.id === adrId);

      // Get the generated content for feedback capture
      let generatedContent = '';
      try {
        generatedContent = await adrService.getADRGeneratedContent(Number.parseInt(projectId, 10), adrId);
      } catch (contentError) {
        logger.warn('Could not retrieve generated content for feedback:', contentError);
      }

      const processingTime = Date.now() - startTime;

      // Capture successful output data for feedback including generated content
      const outputData = {
        success: true,
        contentGenerated: true,
        processingTime,
        timestamp: new Date().toISOString(),
        generatedContent,
        contentLength: generatedContent.length,
        adrStatus: def?.status ?? null,
      };

      // Store generation data for feedback
      adrState.setAdrGenerationData(prev => ({
        ...prev,
        [adrId]: {
          inputs: inputData,
          outputs: outputData,
        },
      }));

      if (def) {
        adrState.setDefinitions((prev: ADRDefinition[]) => prev.map((d: ADRDefinition) => (d.id === def.id ? def : d)));
        const backendTimestamp = def.updatedDate;
        notifyAdrUpdated(Number.parseInt(projectId, 10), adrId, backendTimestamp);
        try {
          const { projectStateService } = await import('../../services/projectStateService');
          await projectStateService.updateAdrActionTimestamp(Number.parseInt(projectId, 10), backendTimestamp);
        } catch (persistErr) {
          logger.warn('Failed to persist ADR generation timestamp:', persistErr);
        }
      }
    } catch (error) {
      logger.error('Failed to generate ADR:', error);
      adrState.showErrorMessage('Failed to generate ADR. Please try again.');

      const processingTime = Date.now() - startTime;
      const adrDefinition = adrState.definitions.find((d: ADRDefinition) => d.id === adrId);

      // Capture error data for feedback
      if (adrDefinition) {
        const inputData = {
          projectId: Number.parseInt(projectId!, 10),
          adrDefinition,
          timestamp: new Date().toISOString(),
          userInitiated: true,
        };

        const outputData = {
          success: false,
          contentGenerated: false,
          processingTime,
          timestamp: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error',
        };

        adrState.setAdrGenerationData(prev => ({
          ...prev,
          [adrId]: {
            inputs: inputData,
            outputs: outputData,
          },
        }));
      }
    } finally {
      adrState.setGeneratingADRIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(adrId);
        return newSet;
      });
    }
  };

  const handleGenerateAllADRs = async () => {
    const definedADRs = adrState.definitions.filter((d: ADRDefinition) => d.status === ADRDefinitionStatus.DEFINED);

    // Early return if no defined ADRs
    if (definedADRs.length === 0) {return;}

    adrState.setIsGeneratingADRs(true);

    try {
      await Promise.all(definedADRs.map((adr: ADRDefinition) => handleGenerateSingleADR(adr.id)));
    } catch (error) {
      logger.error('Failed to generate some ADRs:', error);
    } finally {
      adrState.setIsGeneratingADRs(false);
    }
  };

  const handleDeleteDefinition = async (definitionId: string) => {
    if (!projectId) {return;}

    try {
      await adrService.deleteADRDefinition(Number.parseInt(projectId, 10), definitionId);
      adrState.setDefinitions((prev: ADRDefinition[]) => prev.filter((d: ADRDefinition) => d.id !== definitionId));
      const deletionTimestamp = new Date().toISOString();
      notifyAdrUpdated(Number.parseInt(projectId, 10), definitionId, deletionTimestamp);

      // Also persist the timestamp in project state for cross-page consistency
      const { projectStateService } = await import('../../services/projectStateService');
      await projectStateService.updateAdrActionTimestamp(Number.parseInt(projectId, 10), deletionTimestamp);
    } catch {
      // Silently fail - error message shown to user via showErrorMessage
      adrState.showErrorMessage('Failed to delete ADR definition. Please try again.');
    }
  };

  /**
   * Delete an accepted ADR from the My ADRs table after user confirmation.
   * Reuses the shared deletion logic so project state stays consistent.
   */
  const handleDeleteADRFromTable = async (definition: ADRDefinition) => {
    const confirmed = globalThis.confirm(
      `Delete ADR "${definition.title}"? This action cannot be undone.`,
    );
    if (!confirmed) {return;}
    await handleDeleteDefinition(definition.id);
  };

  const handleViewGeneratedADR = async (definition: ADRDefinition) => {
    adrState.setEditingADR(definition);
    const content = await getADRContent(definition.id);
    adrState.setAdrContent(content);
    adrState.setShowADRViewer(true);
  };

  const handleRegenerateClick = (definition: ADRDefinition) => {
    // If already generated, show confirmation
    if (definition.status === ADRDefinitionStatus.GENERATED) {
      adrState.setRegenerateADRId(definition.id);
      adrState.setShowRegenerateConfirm(true);
    } else {
      // If not generated yet, just generate
      handleGenerateSingleADR(definition.id);
    }
  };

  const handleConfirmRegenerate = () => {
    if (adrState.regenerateADRId) {
      handleGenerateSingleADR(adrState.regenerateADRId);
    }
    adrState.setShowRegenerateConfirm(false);
    adrState.setRegenerateADRId(null);
  };

  const handleCancelRegenerate = () => {
    adrState.setShowRegenerateConfirm(false);
    adrState.setRegenerateADRId(null);
  };

  // Render helper functions
  const renderSuggestionCard = (suggestion: ADRSuggestion) => (
    <div key={suggestion.id} className="bg-white border border-gray-200 rounded-lg shadow-lg hover:shadow-xl transition-shadow p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <span className="text-2xl mr-3">📋</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{suggestion.title}</h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceColor(suggestion.confidenceScore)}`}>
              {getConfidenceLabel(suggestion.confidenceScore)} confidence ({suggestion.confidenceScore}%)
            </span>
          </div>
        </div>
      </div>
      
      <p className="text-gray-700 mb-4">{suggestion.description}</p>
      
      <div className="bg-gray-50 p-3 rounded-lg mb-4">
        <p className="text-sm text-gray-700 mb-2">
          <strong>AI Reasoning:</strong> {suggestion.reasoning}
        </p>
        <p className="text-sm text-gray-600">
          <strong>Source:</strong> {suggestion.contextSource}
        </p>
      </div>
      
      <div className="flex items-center justify-between">
        <FeedbackButtons
          messageId={suggestion.id}
          projectId={Number.parseInt(projectId ?? String(DEFAULT_PROJECT_ID), 10)}
          feedbackContext={{
            type: 'adr-suggestion',
            data: {
              suggestionId: suggestion.id,
              title: suggestion.title,
              description: suggestion.description,
              confidenceScore: suggestion.confidenceScore,
              contextSource: suggestion.contextSource,
            },
          }}
        />
        <div className="flex gap-3">
          <button 
            className={BUTTON_STYLES.secondary}
            onClick={() => handleSkipSuggestion(suggestion.id)}
          >
            <XMarkIcon className="w-4 h-4 mr-2" />
            Skip
          </button>
          <button 
            className={`${BUTTON_STYLES.primary} main-button`}
            onClick={() => handleAcceptSuggestion(suggestion)}
          >
            <CheckIcon className="w-4 h-4 mr-2" />
            Accept & Add
          </button>
        </div>
      </div>
    </div>
  );

  const renderDefinitionCard = (definition: ADRDefinition) => (
    <div key={definition.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-3">📋</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{definition.title}</h3>
              <div className="flex items-center gap-3 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(definition.status)}`}>
                  {definition.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          
          <p className="text-gray-700 mb-3">{definition.contextDescription}</p>
          
          <div className="bg-gray-50 p-3 rounded-lg mb-3">
            <p className="text-sm text-gray-700">
              <strong>LLM Instructions:</strong> {definition.llmInstructions}
            </p>
          </div>

          <p className="text-sm text-gray-500">
            Last updated: {definition.updatedDate ? new Date(definition.updatedDate).toLocaleString() : 'Date not available'}
          </p>
        </div>
        
        <div className="flex items-center gap-2 ml-4">
          <button 
            className={`inline-flex items-center p-2 border rounded transition-colors ${
              definition.status === ADRDefinitionStatus.GENERATED
                ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
            }`}
            onClick={() => definition.status === ADRDefinitionStatus.GENERATED && handleViewGeneratedADR(definition)}
            disabled={definition.status !== ADRDefinitionStatus.GENERATED}
            title={definition.status === ADRDefinitionStatus.GENERATED ? 'View Generated ADR' : 'ADR not generated yet'}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button 
            className={`inline-flex items-center p-2 border rounded transition-colors ${
              isADRGenerating(definition.id, adrState.generatingADRIds)
                ? 'border-blue-300 text-blue-700 bg-blue-50 cursor-not-allowed'
                : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
            }`}
            onClick={() => handleRegenerateClick(definition)}
            disabled={isADRGenerating(definition.id, adrState.generatingADRIds)}
            title={isADRGenerating(definition.id, adrState.generatingADRIds) ? 'Generating ADR...' : 'Generate ADR'}
          >
            {isADRGenerating(definition.id, adrState.generatingADRIds) ? (
              <ArrowPathIcon className={`w-4 h-4 ${ANIMATE_SPIN_CLASS}`} />
            ) : (
              <PlayIcon className="w-4 h-4" />
            )}
          </button>
          <button 
            className="inline-flex items-center p-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            onClick={() => openEditDefinition(definition)}
            title="Edit Definition"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button 
            className="inline-flex items-center p-2 border border-red-300 rounded text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
            onClick={() => handleDeleteDefinition(definition.id)}
            title="Delete Definition"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
  const getStatusColor = (status: ADRDefinitionStatus) => {
    const colors = {
      [ADRDefinitionStatus.DEFINED]: 'bg-blue-100 text-blue-800 border-blue-200',
      [ADRDefinitionStatus.GENERATED]: 'bg-green-100 text-green-800 border-green-200',
      [ADRDefinitionStatus.IN_REVIEW]: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return colors[status];
  };

  const getConfidenceColor = (confidenceScore: number) => {
    if (confidenceScore >= 80) {return 'bg-green-100 text-green-800';}
    if (confidenceScore >= 60) {return 'bg-yellow-100 text-yellow-800';}
    return 'bg-red-100 text-red-800';
  };

  const getConfidenceLabel = (confidenceScore: number) => {
    if (confidenceScore >= 80) {return 'high';}
    if (confidenceScore >= 60) {return 'medium';}
    return 'low';
  };

  const isFormValid = () => {
    return adrState.formData.title.trim() && adrState.formData.contextDescription.trim() && adrState.formData.llmInstructions.trim();
  };

  const canProceedToNextStage = () => {
    return adrState.definitions.length > 0 && adrState.definitions.every((d: ADRDefinition) => d.status === ADRDefinitionStatus.GENERATED);
  };

  const getProceedTooltip = () => {
    if (adrState.definitions.length === 0) {
      return 'Create at least one ADR definition to proceed';
    }
    if (adrState.definitions.some((d: ADRDefinition) => d.status !== ADRDefinitionStatus.GENERATED)) {
      return 'All ADR definitions must be generated before proceeding to architecture analysis';
    }
    return undefined;
  };

  // Extract nested ternary for save button class
  const getSaveButtonClass = (): string => {
    if (adrState.isSaving) {
      return 'loading';
    }
    if (adrState.hasUnsavedChanges) {
      return '';
    }
    return 'btn-outline';
  };

  // Extract nested ternary for last saved span class
  const getLastSavedClass = (): string => {
    if (adrState.justSaved) {
      return 'text-green-600 font-semibold scale-105';
    }
    return 'text-gray-500';
  };

  // Extract nested ternary for save button text
  const getSaveButtonText = (): string => {
    if (adrState.isSaving) {
      return 'Saving...';
    }
    if (adrState.hasUnsavedChanges) {
      return 'Save Changes';
    }
    return 'Saved';
  };

  // Render the AI suggestions tab content
  const renderSuggestionsTab = () => (
    <div className="p-6">
      {/* Header with Actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Potential Decisions from Context Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">These are architectural decisions that might need to be made based on your project context</p>
        </div>
        <div className="flex gap-3">
          {!adrState.isGeneratingSuggestions && adrState.suggestions.length > 0 && (
            <button 
              className={PRIMARY_DISABLED_BUTTON_CLASS}
              onClick={handleAcceptAllSuggestions}
              disabled={adrState.isAcceptingAll}
            >
              <CheckIcon className={`${ICON_BASE_CLASS} ${adrState.isAcceptingAll ? ANIMATE_SPIN_CLASS : ''}`} />
              {adrState.isAcceptingAll ? 'Accepting...' : `Accept All (${adrState.suggestions.length})`}
            </button>
          )}
          <button 
            className={PRIMARY_DISABLED_BUTTON_CLASS}
            onClick={handleGenerateAISuggestions}
            disabled={adrState.isGeneratingSuggestions}
          >
            <ArrowPathIcon className={`${ICON_BASE_CLASS} ${adrState.isGeneratingSuggestions ? ANIMATE_SPIN_CLASS : ''}`} />
            {adrState.isGeneratingSuggestions ? 'Analyzing...' : 'Refresh Suggestions'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {adrState.isGeneratingSuggestions && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <SparklesIcon className="w-8 h-8 text-blue-600 animate-pulse" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing your project context...</h3>
          <p className="text-gray-600">This may take a few moments</p>
        </div>
      )}

      {/* AI Suggestions Grid */}
      {!adrState.isGeneratingSuggestions && adrState.suggestions.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {adrState.suggestions.map(renderSuggestionCard)}
        </div>
      )}

      {/* Empty State */}
      {!adrState.isGeneratingSuggestions && adrState.suggestions.length === 0 && (
        <div className="text-center py-12">
          <SparklesIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No AI suggestions available</h3>
          <p className="text-gray-600 mb-6">
            Click &apos;Refresh Suggestions&apos; to analyze your project context for potential architectural decisions.
            Accepted suggestions can be found in the &apos;My ADR Definitions&apos; tab.
          </p>
        </div>
      )}
    </div>
  );

  // Render the My ADRs table (accepted ADRs as a 9-column governance table)
  const renderMyADRsTable = () => {
    const definitions = adrState.definitions;
    const headerCellClass =
      'px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-200 bg-gray-50';
    const bodyCellClass =
      'px-3 py-2 text-sm text-gray-800 align-top border-b border-gray-100';
    const editableInputClass =
      'w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-400';

    const renderTextCell = (value: string) => (
      <div className="max-h-24 overflow-y-auto whitespace-pre-wrap min-w-[12rem]">
        {value || <span className="text-gray-400">—</span>}
      </div>
    );

    const renderGovernanceInput = (
      def: ADRDefinition,
      field: 'stepOut' | 'approvalDate' | 'approvedBy',
      placeholder: string,
    ) => (
      <input
        type={field === 'approvalDate' ? 'date' : 'text'}
        className={editableInputClass}
        placeholder={placeholder}
        value={governanceDrafts[def.id]?.[field] ?? ''}
        onChange={(e) => handleGovernanceChange(def.id, field, e.target.value)}
        onBlur={() => handleGovernanceSave(def)}
      />
    );

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">My ADRs</h2>
            <p className="text-sm text-gray-600 mt-1">
              Accepted decisions. Context, Decision and Consequence are generated;
              Step-Out, Approval date and Approved by are yours to fill in.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              className={`${BUTTON_STYLES.primary} main-button`}
              onClick={openNewDefinition}
            >
              <PlusIcon className={ICON_BASE_CLASS} />
              Define New ADR
            </button>
            <button
              className={PRIMARY_DISABLED_BUTTON_CLASS}
              onClick={handleGenerateAllADRs}
              disabled={
                isGenerateAllDisabled(adrState.isGeneratingADRs, adrState.generatingADRIds.size) ||
                !hasDefinedADRs(adrState.definitions)
              }
            >
              <PlayIcon className={`${ICON_BASE_CLASS} ${adrState.isGeneratingADRs ? ANIMATE_SPIN_CLASS : ''}`} />
              {adrState.isGeneratingADRs ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>

        {definitions.length === 0 ? (
          <div className="text-center py-10">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">
              No ADRs yet. Accept a suggestion below and it will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-auto border border-gray-200 rounded-lg max-h-[32rem]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className={headerCellClass}>Ref Nr.</th>
                  <th className={headerCellClass}>Title</th>
                  <th className={headerCellClass}>Status</th>
                  <th className={headerCellClass}>Context</th>
                  <th className={headerCellClass}>Decision</th>
                  <th className={headerCellClass}>Consequence</th>
                  <th className={headerCellClass}>Step-Out</th>
                  <th className={headerCellClass}>Approval date</th>
                  <th className={headerCellClass}>Approved by</th>
                  <th className={`${headerCellClass} text-center`}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {definitions.map((def, index) => {
                  const sections = adrSectionsMap[def.id] ?? {
                    context: '',
                    decision: '',
                    consequence: '',
                  };
                  const isGenerating = adrState.generatingADRIds.has(def.id);
                  return (
                    <tr key={def.id} className="hover:bg-gray-50">
                      <td className={`${bodyCellClass} font-mono whitespace-nowrap`}>
                        {formatADRReference(index + 1)}
                      </td>
                      <td className={bodyCellClass}>
                        <button
                          className="text-left text-red-700 hover:underline font-medium"
                          onClick={() =>
                            def.status === ADRDefinitionStatus.GENERATED &&
                            handleViewGeneratedADR(def)
                          }
                        >
                          {def.title}
                        </button>
                      </td>
                      <td className={`${bodyCellClass} whitespace-nowrap`}>
                        {isGenerating ? (
                          <span className="inline-flex items-center text-blue-600">
                            <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                            Generating
                          </span>
                        ) : (
                          <span className="capitalize">{def.status}</span>
                        )}
                      </td>
                      <td className={bodyCellClass}>{renderTextCell(sections.context)}</td>
                      <td className={bodyCellClass}>{renderTextCell(sections.decision)}</td>
                      <td className={bodyCellClass}>{renderTextCell(sections.consequence)}</td>
                      <td className={`${bodyCellClass} min-w-[10rem]`}>
                        {renderGovernanceInput(def, 'stepOut', 'Add step-out…')}
                      </td>
                      <td className={`${bodyCellClass} min-w-[9rem]`}>
                        {renderGovernanceInput(def, 'approvalDate', '')}
                      </td>
                      <td className={`${bodyCellClass} min-w-[9rem]`}>
                        {renderGovernanceInput(def, 'approvedBy', 'Name…')}
                      </td>
                      <td className={`${bodyCellClass} text-center whitespace-nowrap`}>
                        <button
                          type="button"
                          className={DELETE_ROW_BUTTON_CLASS}
                          onClick={() => handleDeleteADRFromTable(def)}
                          disabled={isGenerating}
                          title="Delete ADR"
                          aria-label={`Delete ADR ${def.title}`}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render the definitions tab content
  const renderDefinitionsTab = () => (
    <div className="p-6">
      {/* Header with Actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Defined ADR Requirements</h2>
          <p className="text-sm text-gray-600 mt-1">These are the architectural decisions you want to generate</p>
        </div>
        <div className="flex gap-3">
          {hasDefinedADRs(adrState.definitions) && (
            <button 
              className={`inline-flex items-center px-4 py-2 text-white rounded-md text-sm transition-colors  main-button ${
                isGenerateAllDisabled(adrState.isGeneratingADRs, adrState.generatingADRIds.size)
                  ? 'opacity-50 cursor-not-allowed  main-button'
                  : ''
              }`}
              onClick={handleGenerateAllADRs}
              disabled={isGenerateAllDisabled(adrState.isGeneratingADRs, adrState.generatingADRIds.size)}
            >
              <PlayIcon className="w-4 h-4 mr-2" />
              Generate All ADRs
            </button>
          )}
          <button 
            className={`${BUTTON_STYLES.primary} main-button`}
            onClick={openNewDefinition}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Define New ADR
          </button>
        </div>
      </div>

      {/* Loading State */}
      {adrState.isLoadingDefinitions && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <DocumentTextIcon className="w-8 h-8 text-blue-600 animate-pulse" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading ADR definitions...</h3>
          <p className="text-gray-600">Please wait while we fetch your data</p>
        </div>
      )}

      {/* Definitions List */}
      {!adrState.isLoadingDefinitions && adrState.definitions.length > 0 ? (
        <div className="space-y-4">
          {adrState.definitions.map(renderDefinitionCard)}
        </div>
      ) : !adrState.isLoadingDefinitions && (
        <div className="text-center py-12">
          <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No ADR definitions yet</h3>
          <p className="text-gray-600 mb-6">
            Start by accepting AI suggestions or defining your own architectural decisions
          </p>
          <button 
            className={`${BUTTON_STYLES.primary} main-button`}
            onClick={openNewDefinition}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Define First ADR
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Stage Navigation */}
      <StageNavigationBar 
        projectId={projectId ? Number(projectId) : DEFAULT_PROJECT_ID} 
        currentStage={4}
        isBrownfield={adrState.project?.projectType === ProjectType.BROWN_FIELD}
        projectStatus={ProjectStatus.CONTEXT_REVIEW}
        onStageClick={handleStageClick}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <PageHeader
          title="Architecture Decision Records"
          subtitle={`Define potential architectural decisions and generate ADRs with AI assistance for ${adrState.project?.title ?? YOUR_PROJECT_TEXT}`}
          icon={DocumentTextIcon}
          iconBgColor="#DC2626"
        />

        {/* AI Disclaimer */}
        <AIDisclaimer className="mb-6" />

        {/* My ADRs table (accepted decisions) on top */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          {renderMyADRsTable()}
        </div>

        {/* AI Suggested ADRs below — accepting moves a suggestion up into the
            My ADRs table and auto-generates it. */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {renderSuggestionsTab()}
        </div>

        {/* Execution Plan Panel */}
        <div className="mb-6">
          <ExecutionPlanPanel
            title="ADR Suggestion Execution Plan"
            executionPlan={adrExecutionPlan}
            isLoading={false}
            isExpanded={isExecutionPlanExpanded}
            onToggleExpanded={() => setIsExecutionPlanExpanded(!isExecutionPlanExpanded)}
          />
        </div>

        {/* Bottom Navigation */}
        <BottomNavigationBar
          backAction={{
            label: BACK_BUTTON_LABEL,
            onClick: () => handleStageClick(3, `/project/${projectId}/cra`),
          }}
          primaryActions={[
            {
              label: CONTINUE_BUTTON_LABEL,
              onClick: async () => {
                if (projectId) {
                  const { projectStateService } = await import(
                    '../../services/projectStateService'
                  );
                  await projectStateService.advanceProjectStatus(
                    Number(projectId),
                    ProjectStatus.GENERATION,
                  );
                }
                handleStageClick(5, `/project/${projectId}/generation`);
              },
              disabled: !canProceedToNextStage(),
              tooltip: getProceedTooltip(),
              variant: 'primary',
            },
          ]}
        />
      </div>

      {/* ADR Definition Modal */}
      {adrState.showDefinitionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {adrState.editingDefinition ? 'Edit ADR Definition' : 'Define New ADR Requirement'}
              </h3>
              <button 
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={closeDefinitionModal}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Decision Title <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text"
                    placeholder="e.g., Database Technology Selection"
                    className={INPUT_STYLES.base}
                    value={adrState.formData.title}
                    onChange={(e) => adrState.setFormData({ ...adrState.formData, title: e.target.value })}
                  />
                </div>
              </div>

              {/* Context Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Context Description <span className="text-red-500">*</span>
                </label>
                <textarea 
                  placeholder="Describe why this decision is needed and the background context..."
                  className={INPUT_STYLES.textarea}
                  rows={3}
                  value={adrState.formData.contextDescription}
                  onChange={(e) => adrState.setFormData({ ...adrState.formData, contextDescription: e.target.value })}
                />
              </div>

              {/* LLM Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LLM Generation Instructions <span className="text-red-500">*</span>
                </label>
                <textarea 
                  placeholder="Provide detailed instructions for the AI to generate this ADR. Include what to analyze, what options to consider, and what factors to evaluate..."
                  className={INPUT_STYLES.textarea}
                  rows={4}
                  value={adrState.formData.llmInstructions}
                  onChange={(e) => adrState.setFormData({ ...adrState.formData, llmInstructions: e.target.value })}
                />
                <p className="mt-1 text-sm text-gray-500">
                  These instructions will guide the AI when generating the actual ADR content
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button 
                className={BUTTON_STYLES.secondary}
                onClick={closeDefinitionModal}
              >
                Cancel
              </button>
              <button 
                className='px-6 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors main-button'
                onClick={handleFormSubmit}
                disabled={!isFormValid()}
              >
                {adrState.editingDefinition ? 'Update Definition' : 'Create Definition'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADR Editor Modal with Monaco */}
      {adrState.showADRViewer && adrState.editingADR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{adrState.editingADR.title}</h3>
                <p className="text-sm text-gray-600">{adrState.editingADR.id}</p>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={handleSaveADR}
                    disabled={!adrState.hasUnsavedChanges || adrState.isSaving}
                    className={`btn btn-sm main-button ${getSaveButtonClass()}`}
                  >
                    <CloudArrowUpIcon className={`${ICON_BASE_CLASS} ${adrState.isSaving ? ANIMATE_SPIN_CLASS : ''}`} />
                    {getSaveButtonText()}
                  </button>
                  <span className={`text-sm transition-all duration-500 ${getLastSavedClass()}`}>
                    Last saved: {adrState.lastSaved.toLocaleTimeString()}
                    {adrState.justSaved && (
                      <span className="ml-2 text-green-600 animate-pulse">
                        ✓ Saved!
                      </span>
                    )}
                  </span>
                  {/* Feedback for Generated ADR Content */}
                  {adrState.adrGenerationData[adrState.editingADR.id]?.outputs?.success && adrState.adrGenerationData[adrState.editingADR.id]?.inputs?.userInitiated && (
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-xs text-gray-500">Rate this ADR:</span>
                      <FeedbackButtons 
                        messageId={adrState.editingADR.id}
                        projectId={Number.parseInt(projectId ?? '1', 10)}
                        feedbackContext={{
                          type: 'custom',
                          data: {
                            feedbackType: 'adr-generation',
                            adrId: adrState.editingADR.id,
                            title: adrState.editingADR.title,
                            content: adrState.editingADR.llmInstructions,
                            status: adrState.editingADR.status,
                            generationInputs: adrState.adrGenerationData[adrState.editingADR.id]?.inputs,
                            generationOutputs: adrState.adrGenerationData[adrState.editingADR.id]?.outputs,
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <button 
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={handleCloseADRViewer}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Monaco Editor */}
            <div className="flex-1 min-h-0 p-6">
              <MonacoMarkdownEditor
                value={adrState.adrContent}
                onChange={handleADRContentChange}
                height="500px"
                theme="light"
                placeholder="Edit your ADR content..."
                iconOnlyButtons={false}
                defaultViewMode="preview"
                previewOnly={false}
              />
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button 
                className={BUTTON_STYLES.secondary}
                onClick={handleCloseADRViewer}
              >
                Close
              </button>
              <button 
                onClick={handleSaveADR}
                disabled={adrState.isSaving}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors main-button ${
                  adrState.isSaving 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : ''
                }`}
              >
                {adrState.isSaving ? (
                  <>
                    <CloudArrowUpIcon className={`w-4 h-4 mr-2 ${ANIMATE_SPIN_CLASS} inline`} />
                    Saving...
                  </>
                ) : (
                  'Save ADR'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Confirmation Modal */}
      {adrState.showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Confirm Regeneration</h3>
              <button 
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={handleCancelRegenerate}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700">
                This ADR has already been generated. Regenerating it will overwrite the existing content and any manual changes you made will be lost.
              </p>
              <p className="text-gray-700 mt-4 font-semibold">
                Are you sure you want to continue?
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button 
                className={BUTTON_STYLES.secondary}
                onClick={handleCancelRegenerate}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                onClick={handleConfirmRegenerate}
              >
                Confirm Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {adrState.showError && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <XMarkIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-1 text-sm text-red-700">
                  {adrState.errorMessage}
                </div>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                    onClick={() => {
                      adrState.setShowError(false);
                      adrState.setErrorMessage('');
                    }}
                  >
                    <span className="sr-only">Dismiss</span>
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ADRDefinitionPage;
